import React, { useEffect, useState } from 'react';
import { Box, Typography, Card, CardContent, Collapse, IconButton, CircularProgress, List, ListItemText, ListItemButton, Divider, Button, Grid, Paper } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Delete } from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const POSTHOG_API_KEY = 'phc_WOgFVcm9eahQVCFx8NKBeHpjdCEzYVuA7LH0xhoIUiY';
const POSTHOG_PROJECT_ID = 'your-project-id'; // Optional, not needed for public stats
const POSTHOG_API_URL = 'https://app.posthog.com/api';

const AdminPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState({});
  const [phLoading, setPhLoading] = useState(true);
  const [phError, setPhError] = useState('');
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'https://pathly-backend-tqce.onrender.com';
        const token = localStorage.getItem('access_token');
        const res = await fetch(`${API_URL}/admin`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (!res.ok) {
          let detail = 'Not authorized or failed to fetch';
          try {
            const json = await res.json();
            detail = json.detail || detail;
          } catch {
            detail = res.statusText || detail;
          }
          throw new Error(detail);
        }
        const json = await res.json();
        setData(json);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    // Fetch PostHog daily visitors and page views for the last 30 days
    const fetchPostHogGraphs = async () => {
      setPhLoading(true);
      setPhError('');
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'https://pathly-backend-tqce.onrender.com';
        const token = localStorage.getItem('access_token');
        const res = await fetch(`${API_URL}/admin/analytics`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (!res.ok) throw new Error('Failed to fetch analytics');
        const data = await res.json();
        const chart = (data.labels || []).map((label, i) => ({
          date: label,
          pageViews: data.pageViews[i] || 0,
          visitors: data.visitors[i] || 0
        }));
        setChartData(chart);
      } catch (err) {
        setPhError('Failed to fetch analytics');
      } finally {
        setPhLoading(false);
      }
    };
    fetchPostHogGraphs();
  }, []);

  const handleExpand = id => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to remove this person from the waitlist?')) return;
    setLoading(true);
    setError('');
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://pathly-backend-tqce.onrender.com';
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${API_URL}/waitlist/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!res.ok) {
        let detail = 'Failed to remove from waitlist';
        try {
          const json = await res.json();
          detail = json.detail || detail;
        } catch {
          detail = res.statusText || detail;
        }
        throw new Error(detail);
      }
      setData(prev => ({
        ...prev,
        waitlist: prev.waitlist.filter(entry => entry.id !== id),
        total: prev.total - 1
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Box sx={{ p: 8, textAlign: 'center' }}><CircularProgress /></Box>;
  if (error) return <Box sx={{ p: 8, textAlign: 'center' }}><Typography color="error">{error}</Typography></Box>;
  if (!data) return null;

  return (
    <Box>
      {/* Gradient text header, no block background */}
      <Box sx={{ width: '100%', py: 3, mb: 4, paddingTop: '64px', textAlign: 'center' }}>
        <Typography
          variant="h3"
          fontWeight={900}
          sx={{
            background: 'linear-gradient(90deg, #ff9966 0%, #ff5e62 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            color: 'transparent',
            letterSpacing: 1,
            display: 'inline-block',
          }}
        >
          Admin Dashboard
        </Typography>
      </Box>
      <Box sx={{ maxWidth: 900, mx: 'auto', p: 4 }}>
        <Card sx={{ mb: 4, p: 3, bgcolor: '#f5faff' }}>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
            Waitlist Signups: {data.total}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {data.waitlist.length === 0 ? (
            <Typography sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>
              No one is on the waitlist yet.
            </Typography>
          ) : (
            <List>
              {data.waitlist.map(entry => (
                <React.Fragment key={entry.id}>
                  <ListItemButton onClick={() => handleExpand(entry.id)}>
                    <ListItemText primary={entry.name} />
                    <IconButton size="small" onClick={e => { e.stopPropagation(); handleExpand(entry.id); }}>
                      <ExpandMoreIcon sx={{ transform: expanded[entry.id] ? 'rotate(180deg)' : 'rotate(0deg)', transition: '0.2s' }} />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={e => { e.stopPropagation(); handleDelete(entry.id); }}>
                      <Delete />
                    </IconButton>
                  </ListItemButton>
                  <Collapse in={expanded[entry.id]} timeout="auto" unmountOnExit>
                    <CardContent sx={{ bgcolor: '#e3f2fd', borderRadius: 2, mb: 2 }}>
                      <Typography><b>Email:</b> {entry.email}</Typography>
                      <Typography><b>Grade Level:</b> {entry.grade_level}</Typography>
                      <Typography variant="caption" color="text.secondary"><b>Signed up:</b> {new Date(entry.created_at).toLocaleString()}</Typography>
                    </CardContent>
                  </Collapse>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          )}
        </Card>
      </Box>
    </Box>
  );
};

export default AdminPage; 